import React, { createContext, useContext, useMemo, useState } from 'react';
import SearchSource from '~/constants/SearchSource';
import { Nullable } from '~/types';

export type KeywordSuggestionEventData = Nullable<{
  buildKey: number;
  isStickySearchbar: boolean;
  result: string;
  suggestionIndex: number;
  suggestionsLength: number;
  suggestionText: string;
  textEntered: string;
  textSource: string;
}>;

export type KeywordSuggestionContext = Partial<KeywordSuggestionEventData>;

type ContextType = {
  keywordSuggestion: KeywordSuggestionContext;
  refSource: string;
  searchSource: SearchSource;
  setKeywordSuggestion: React.Dispatch<
    React.SetStateAction<KeywordSuggestionContext>
  >;
  setRefSource: React.Dispatch<string>;
  setSearchSource: React.Dispatch<React.SetStateAction<SearchSource>>;
};

const defaultContext: ContextType = {
  keywordSuggestion: {},
  refSource: '',
  searchSource: SearchSource.InitialPageLoad,
  setKeywordSuggestion() {},
  setRefSource() {},
  setSearchSource() {}
};

const SearchEventsDataContext = createContext<ContextType>(defaultContext);

export type OwnProps = React.PropsWithChildren<{
  refSource: string | string[];
  keywordSuggestion: KeywordSuggestionContext;
}>;

export function SearchEventsDataContextProvider({ children, ...props }) {
  const [searchSource, setSearchSource] = useState<SearchSource>(
    defaultContext.searchSource
  );

  const [refSource, setRefSource] = useState<string>(props.refSource);

  const [keywordSuggestion, setKeywordSuggestion] =
    useState<KeywordSuggestionContext>(props.keywordSuggestion ?? {});

  const value = useMemo<ContextType>(
    (): ContextType => ({
      keywordSuggestion,
      searchSource,
      refSource,
      setKeywordSuggestion,
      setRefSource,
      setSearchSource
    }),
    [keywordSuggestion, searchSource, refSource]
  );

  return (
    <SearchEventsDataContext.Provider value={value}>
      {children}
    </SearchEventsDataContext.Provider>
  );
}

export function useKeywordSuggestion() {
  return useContext(SearchEventsDataContext).keywordSuggestion;
}

export function useSearchSource() {
  return useContext(SearchEventsDataContext).searchSource;
}

export function useRefSource() {
  return useContext(SearchEventsDataContext).refSource;
}

export default SearchEventsDataContext;
